/**
 * What's in the basket so far, for the running summary shown through the
 * intake flow (TypeSelection → Alteration → PerfectFit → ItemDetail) and not
 * just on the checkout screen.
 *
 * The cart keeps items and services in two flat arrays: `itemsData` holds the
 * garments (`id`, `name`, `pricing_plan`) and `services` holds the work, each
 * pointing back at its garment through `item_details.id`. Staff read the
 * basket by garment ("the sweater has three things on it"), so this pairs
 * them back up.
 *
 * Pure — no store, no React — so the same shape can be rendered anywhere.
 */

import {
  hasNoEachExtra,
  pairFamily,
  shortServiceName,
} from "./serviceOptions.js";

/** The price actually charged for a service, given its garment's plan. */
export const servicePrice = (service, plan) => {
  const amount =
    plan === "Premium" ? service?.premium_price : service?.standard_price;
  const value = Number(amount);
  return Number.isFinite(value) ? value : 0;
};

/**
 * Garments in the basket with the work booked on each, in the order they were
 * added. A garment with no services yet (the one being built right now) is
 * kept, so the summary shows it as in progress rather than hiding it.
 */
export const cartGarments = (cart) => {
  const items = cart?.itemsData || [];
  const services = cart?.services || [];

  const byItem = new Map();
  for (const item of items) {
    if (!item?.id) continue;
    byItem.set(item.id, {
      id: item.id,
      name: item.name || "Item",
      plan: item.pricing_plan || "Standard",
      services: [],
      total: 0,
    });
  }

  for (const service of services) {
    const id = service?.item_details?.id;
    const garment = byItem.get(id);
    // A service whose garment is gone from itemsData would otherwise vanish
    // from the summary while still being charged at checkout.
    if (!garment) continue;
    const price = servicePrice(service, garment.plan);
    garment.services.push({ name: service?.name || "Service", price });
    garment.total += price;
  }

  return [...byItem.values()];
};

/** Services subtotal, matching what the checkout screen adds up. */
export const cartSubtotal = (cart) =>
  cartGarments(cart).reduce((sum, garment) => sum + garment.total, 0);

/** How many pieces of work are booked (what checkout calls "items"). */
export const cartServiceCount = (cart) => (cart?.services || []).length;

/** Is there a finished basket to go back to? Checkout needs at least one
 *  garment with at least one service on it. */
export const cartIsCheckoutable = (cart) =>
  cartGarments(cart).some((garment) => garment.services.length > 0);

/**
 * The cart with one service taken off, by its position in `services`, for the
 * remove button on the checkout summary. If that was the garment's last
 * service the garment goes too, so checkout never posts a garment with no
 * work on it. Taking off the only service leaves both arrays empty, which the
 * checkout's cart guard treats as an empty basket.
 */
export const withoutService = (cart, index) => {
  const services = cart?.services || [];
  const removed = services[index];
  const kept = services.filter((_, i) => i !== index);
  const garmentId = removed?.item_details?.id;
  const garmentStillBooked = kept.some(
    (service) => service?.item_details?.id === garmentId,
  );
  const itemsData = (cart?.itemsData || []).filter(
    (item) => garmentStillBooked || item?.id !== garmentId,
  );
  // Taking a row out can leave a first-one behind, so the survivors are
  // re-tiered here rather than at each of the four × buttons.
  const repriced = repricePairs({ itemsData, services: kept });
  return { itemsData: repriced.itemsData, services: repriced.services };
};

/**
 * Re-price the pair families in a cart, so the FIRST row of each family on
 * each garment carries the first-one price and every row after it carries the
 * each-extra price.
 *
 * The pricing tier is a position, not a property of the hole. A customer
 * pressing × on "fix one hole" is removing *a* hole, not *the first* hole, so
 * leaving the survivors at the each-extra price charges for extras with
 * nothing to be extra to: $32 for what is now the only hole, $10 under. The
 * tier is derived here on every change instead of being stored once.
 *
 * `first` is the one the customer entered first, not the one that makes the
 * total cheapest. The setup fee is not the same across sizes ($10 on the
 * sweater's standard sizes, $7 on one premium pair, $10 / $18 / $25 across the
 * jeans premium pairs), so cheapest-first would move the price when rows are
 * reordered, which reads as a bug even when it saves them money.
 *
 * Rows with no `pair` are left exactly as they are: a cart built before this
 * shipped carries none, and inventing one without the catalogue is not
 * possible here.
 */
export const repricePairs = (cart) => {
  const seen = new Map();
  const services = (cart?.services || []).map((service) => {
    if (!service?.pair?.first || !service?.pair?.extra) return service;
    // A basket part-filled before denim holes lost their each-extra price
    // still carries the pair it was given. Left alone rather than re-priced,
    // so a line is charged what it said it would be when it was added.
    if (hasNoEachExtra(service.pair.extra.name)) return service;
    const itemName = service?.item_details?.name;
    const key = [
      String(service?.item_details?.id ?? ""),
      pairFamily(shortServiceName(service?.name, itemName)),
    ].join("|");
    const seenBefore = seen.get(key) || 0;
    seen.set(key, seenBefore + 1);
    const want = seenBefore === 0 ? service.pair.first : service.pair.extra;
    if (!want?.name || want.name === service.name) return service;
    return {
      ...service,
      _id: want._id ?? service._id,
      name: want.name,
      standard_price: want.standard_price,
      premium_price: want.premium_price,
    };
  });
  return { ...cart, services };
};

/**
 * What a trip through the service screen is about to add to the bill: the
 * price of each of `count` rows of `row`, as they will be charged once they
 * land in `cart`.
 *
 * The running basket quotes the picked service at the price on the chip,
 * which is right for one and wrong for four. In a first-one/each-extra
 * family only the first row carries the first-one price, and on a garment
 * that already has one of that family every row added now is an each-extra.
 * So the rows are run through the cart's own re-tiering and read back,
 * rather than that arithmetic being done a second way here and drifting.
 */
export const addedPrices = (cart, row, count, plan) => {
  const howMany = Math.max(1, Math.floor(Number(count) || 1));
  const rows = Array.from({ length: howMany }, () => row);
  const priced = repricePairs({
    ...cart,
    services: [...(cart?.services || []), ...rows],
  }).services;
  return priced.slice(-howMany).map((service) => servicePrice(service, plan));
};
